import { Action, Dispatch } from 'redux';
import { ProviderSchedules, SearchProvider } from './types';
import { ReduxAction, failure, pending, success } from '../core';

import { HealAPI } from '../../healApi';
import { StatusOptions } from '../../types';
import { ThunkAction } from 'redux-thunk';
import { toast } from '../message/actions';
import { fetchDoctors as fetchDoctorsApi, fetchAssistants as fetchAssistantsApi } from '../../api/providers';
import { ProviderData, RouteDetails } from '../../api/providers.types';

export class ProvidersAction {
  public static PROVIDER_SEARCH_CHANGED = `PROVIDERS_SEARCH_CHANGED`;
  public static PROVIDER_SET_SEARCHING = `PROVIDERS_SET_SEARCHING`;
  public static PROVIDER_SET_PROVIDERS = `PROVIDERS_SET_PROVIDERS`;

  public static SET_PROVIDER_SCHEDULES_FETCHING = `PROVIDERS_SET_PROVIDER_SCHEDULES_FETCHING`;
  public static SET_PROVIDER_SCHEDULES = `PROVIDERS_SET_PROVIDER_SCHEDULES`;

  public static SET_PROVIDER_LOCATION_HISTORY_FETCHING = `PROVIDERS_SET_PROVIDER_LOCATION_HISTORY_FETCHING`;
  public static SET_PROVIDER_LOCATION_HISTORY = `PROVIDERS_SET_PROVIDER_LOCATION_HISTORY`;

  public static SET_PROVIDER_ON_CALL = `PROVIDERS_SET_PROVIDER_ON_CALL`;
  public static SET_PROVIDER_OFF_CALL = `PROVIDERS_SET_PROVIDER_OFF_CALL`;

  public static FETCH_DOCTORS = `PROVIDERS_FETCH_DOCTORS`;
  public static FETCH_ASSISTANTS = `PROVIDERS_FETCH_ASSISTANTS`;

  public static SET_DOCTORS = `PROVIDERS_SET_DOCTORS`;
  public static SET_ASSISTANTS = `PROVIDERS_SET_ASSISTANTS`;

  public static SET_IS_FETCHING_ALL_PROVIDERS = `PROVIDERS_SET_IS_FETCHING_ALL_PROVIDERS`;
  public static SET_ALL_PROVIDERS = `PROVIDERS_SET_ALL_PROVIDERS`;
  public static SET_ALL_PROVIDERS_ERROR = `PROVIDERS_SET_ALL_PROVIDERS_ERROR`;

  public static CREATE_BREAK = `PROVIDERS_CREATE_BREAK`;
  public static EDIT_BREAK = `PROVIDERS_EDIT_BREAK`;
  public static DELETE_BREAK = `PROVIDERS_DELETE_BREAK`;
}

export function providerSearchChanged(newValue: string): ReduxAction<string> {
  return {
    type: ProvidersAction.PROVIDER_SEARCH_CHANGED,
    data: newValue,
  };
}

export function setIsSearching(newValue: boolean): ReduxAction<boolean> {
  return {
    type: ProvidersAction.PROVIDER_SET_SEARCHING,
    data: newValue,
  };
}

export function setProviders(providers: SearchProvider[]): ReduxAction<SearchProvider[]> {
  return {
    type: ProvidersAction.PROVIDER_SET_PROVIDERS,
    data: providers,
  };
}

export function setIsProviderSchedulesFetching(marketCode: string, value: boolean): ReduxAction<{ marketCode: string; value: boolean }> {
  return {
    type: ProvidersAction.SET_PROVIDER_SCHEDULES_FETCHING,
    data: {
      marketCode,
      value,
    },
  };
}

export function setProviderSchedules(marketCode: string, value: ProviderSchedules[]): ReduxAction<{ marketCode: string; value: ProviderSchedules[] }> {
  return {
    type: ProvidersAction.SET_PROVIDER_SCHEDULES,
    data: {
      marketCode,
      value,
    },
  };
}

export function setProviderLocationHistoryFetching(providerId: string, value: boolean): ReduxAction<{ providerId: string; value: boolean }> {
  return {
    type: ProvidersAction.SET_PROVIDER_LOCATION_HISTORY_FETCHING,
    data: {
      providerId,
      value,
    },
  };
}

export function setProviderLocationHistory(providerId: string, value: RouteDetails): ReduxAction<{ providerId: string; value: RouteDetails }> {
  return {
    type: ProvidersAction.SET_PROVIDER_LOCATION_HISTORY,
    data: {
      providerId,
      value,
    },
  };
}

export function fetchAllProviders(value: boolean): ReduxAction<boolean> {
  return {
    type: ProvidersAction.SET_IS_FETCHING_ALL_PROVIDERS,
    data: value,
  };
}

export function setAllProviders(value: ProviderData[]): ReduxAction<ProviderData[]> {
  return {
    type: ProvidersAction.SET_ALL_PROVIDERS,
    data: value,
  };
}

export function setAllProvidersError(value: string): ReduxAction<string> {
  return {
    type: ProvidersAction.SET_ALL_PROVIDERS_ERROR,
    data: value,
  };
}

export function setProviderOnCall(providerId: string, marketCode: string, providerName: string): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ProvidersAction.SET_PROVIDER_SCHEDULES_FETCHING,
      data: { marketCode, value: true },
    });
    return HealAPI.setProviderOnCall(providerId).then((response) => {
      if (response.ok && response.data && response.data.status === StatusOptions.OK) {
        dispatch({
          type: ProvidersAction.SET_PROVIDER_ON_CALL,
          data: { providerId, marketCode },
        });
        dispatch(toast({ type: 'success', title: 'Success', content: providerName + 'is now ON CALL' }));
      } else {
        dispatch(toast({ type: 'error', title: 'Error', content: 'There was an error trying to set ' + providerName + ' ON CALL, please try again' }));
      }
      dispatch({
        type: ProvidersAction.SET_PROVIDER_SCHEDULES_FETCHING,
        data: { marketCode, value: false },
      });
    });
  };
}

export function setProviderOffCall(providerId: string, marketCode: string, providerName: string): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ProvidersAction.SET_PROVIDER_SCHEDULES_FETCHING,
      data: { marketCode, value: true },
    });
    return HealAPI.setProviderOffCall(providerId).then((response) => {
      if (response.ok && response.data && response.data.status === StatusOptions.OK) {
        dispatch({
          type: ProvidersAction.SET_PROVIDER_OFF_CALL,
          data: { providerId, marketCode },
        });
        dispatch(toast({ type: 'success', title: 'Success', content: providerName + ' is now OFF CALL' }));
      } else {
        dispatch(toast({ type: 'error', title: 'Error', content: 'There was an error trying to set ' + providerName + ' OFF CALL, please try again' }));
      }
      dispatch({
        type: ProvidersAction.SET_PROVIDER_SCHEDULES_FETCHING,
        data: { marketCode, value: false },
      });
    });
  };
}

export function fetchDoctors(serviceCode: string, marketCode: string, day?: number): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(ProvidersAction.FETCH_DOCTORS) });
    return fetchDoctorsApi(serviceCode, marketCode, day).then(
      (data) => {
        dispatch({
          type: success(ProvidersAction.FETCH_DOCTORS),
          data: { value: data },
        });
      },
      (error) => {
        dispatch({ type: failure(ProvidersAction.FETCH_DOCTORS), data: error.message });
      }
    );
  };
}

export function fetchAssistants(serviceCode: string, marketCode: string, day?: number): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(ProvidersAction.FETCH_ASSISTANTS) });
    return fetchAssistantsApi(serviceCode, marketCode, day).then(
      (data) => {
        dispatch({
          type: success(ProvidersAction.FETCH_ASSISTANTS),
          data: { value: data },
        });
      },
      (error) => {
        dispatch({ type: failure(ProvidersAction.FETCH_ASSISTANTS), data: error.message });
      }
    );
  };
}

export function createBreak(doctorId: string, startTime: number, endTime: number, type: string, MAId?: string): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(ProvidersAction.CREATE_BREAK) });
    return HealAPI.createBreak(doctorId, startTime, endTime, type, MAId)
      .then((response) => {
        if (response.ok) {
          dispatch(toast({ type: 'success', title: 'Success', content: 'Break created successfully' }));
          dispatch({ type: success(ProvidersAction.CREATE_BREAK), data: { value: response.data } });
        } else {
          dispatch(
            toast({
              type: 'error',
              title: 'Error',
              content: (response.data || ({} as any)).description || 'Could not connect to server',
            })
          );
        }
      })
      .catch((error) => {
        dispatch(
          toast({
            type: 'error',
            title: 'Error',
            content: error.data.description || 'Could not connect to server',
          })
        );
        dispatch({ type: failure(ProvidersAction.CREATE_BREAK), data: error });
      });
  };
}

export function editBreak(id: string, doctorId: string, startTime: number, endTime: number, type: string, MAId?: string): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(ProvidersAction.EDIT_BREAK) });
    return HealAPI.editBreak(id, doctorId, startTime, endTime, type, MAId)
      .then((response) => {
        if (response.ok) {
          dispatch(toast({ type: 'success', title: 'Success', content: 'Break edited successfully' }));
          dispatch({ type: success(ProvidersAction.EDIT_BREAK), data: { value: response.data } });
        } else {
          dispatch(
            toast({
              type: 'error',
              title: 'Error',
              content: (response.data || ({} as any)).description || 'Could not connect to server',
            })
          );
        }
      })
      .catch((error) => {
        dispatch(
          toast({
            type: 'error',
            title: 'Error',
            content: error.data.description || 'Could not connect to server',
          })
        );
        dispatch({ type: failure(ProvidersAction.EDIT_BREAK), data: error });
      });
  };
}

export function deleteBreak(breakId: string): ThunkAction<any, any, any, any> {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: pending(ProvidersAction.DELETE_BREAK) });
    return HealAPI.deleteBreak(breakId)
      .then((response) => {
        if (response.ok) {
          dispatch(toast({ type: 'success', title: 'Success', content: 'Break deleted successfully' }));
          dispatch({ type: success(ProvidersAction.DELETE_BREAK), data: { value: breakId } });
        } else {
          dispatch(
            toast({
              type: 'error',
              title: 'Error',
              content: (response.data || ({} as any)).description || 'Could not connect to server',
            })
          );
        }
      })
      .catch((error) => {
        dispatch(
          toast({
            type: 'error',
            title: 'Error',
            content: error.data.description || 'Break not found',
          })
        );
        dispatch({ type: failure(ProvidersAction.DELETE_BREAK), data: error });
      });
  };
}
